import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Placeholder } from '@tiptap/extension-placeholder';
import { BubbleMenu, EditorContent, useEditor } from '@tiptap/react';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';
import { editorProps, extensions, imagesExtensions } from './constants.js';
import { applyLink } from './helpers.js';
import { LinkTooltip } from './LinkTooltip.js';
import { Textarea, TextareaWrapper, Wrapper } from './styled.js';
import { Toolbar } from './Toolbar.js';
export const RichTextEditor = forwardRef(function RichTextEditor({ value, onChange, disabled, success, warning, error, readOnly, readOnlyMode, withPreviewToggle, placeholders, linkTooltipPlaceholder = 'Enter a URL', additionalToolbarItems, dataTestId, rows, allowImages, placeholder, ...props }, ref) {
    const [isPreview, setIsPreview] = useState(true);
    const [rawEditorValue, setRawEditorValue] = useState(value ?? '');
    const rawEditorRef = useRef(null);
    const editor = useEditor({
        editable: !readOnly && !disabled,
        extensions: [
            ...extensions,
            ...(allowImages ? imagesExtensions : []),
            Placeholder.configure({
                placeholder,
            }),
        ],
        onBlur: (p) => {
            const html = p.editor.getHTML();
            onChange?.(html);
            setRawEditorValue(html);
        },
        parseOptions: {
            preserveWhitespace: 'full',
        },
        editorProps,
    });
    const handleRawEditorChange = useCallbackRef((e) => {
        const next = e.target.value;
        setRawEditorValue(next);
        onChange?.(next);
    });
    const handleSaveLink = useCallbackRef((href) => {
        if (!editor)
            return;
        applyLink(editor, href);
    });
    // Keep `<BubbleMenu>` mounted across `disabled`/`readOnly` toggles to avoid
    // tippy.js DOM detach/reattach racing with TipTap's ProseMirror DOM mutations,
    // which surfaces as "Failed to execute 'removeChild' on 'Node'" in consumers
    // that flip `disabled` while the editor is mounted. Visibility is gated here.
    // The `isFocused` guard restores TipTap's default focus gating (a custom
    // `shouldShow` replaces it entirely): the Link mark is inclusive, so content
    // that starts with a link reports `isActive('link')` at the document start even
    // when the editor was never focused — without this the tooltip pops up on its
    // own whenever such content is loaded (e.g. a pre-filled report email body).
    const shouldShowLinkTooltip = useCallbackRef(() => !disabled && !readOnly && !!editor?.isFocused && !!editor?.isActive('link'));
    useEffect(() => {
        if (editor && value !== editor.getHTML()) {
            editor.commands.setContent(value ?? '', false);
        }
    }, [editor, value]);
    useEffect(() => {
        if (!editor)
            return;
        if (!readOnly && !disabled) {
            editor.setOptions({ editable: true });
        }
        else {
            editor.setOptions({ editable: false });
        }
    }, [editor, readOnly, disabled]);
    useImperativeHandle(ref, () => ({
        focus: () => {
            if (editor) {
                editor.commands.focus();
            }
        },
        select: () => {
            if (editor) {
                editor.commands.focus();
                editor.commands.selectAll();
            }
        },
    }));
    return (_jsxs(Wrapper, { $inputDisabled: readOnly || disabled, $readOnlyMode: readOnlyMode, $success: success, $warning: warning, $error: error, rows: rows, "data-testid": dataTestId || 'rich-text-editor', children: [editor && !readOnly && (_jsx(Toolbar, { editor: editor, disabled: disabled || readOnly, placeholders: placeholders, additionalToolbarItems: additionalToolbarItems, isPreview: isPreview, setIsPreview: setIsPreview, withPreviewToggle: withPreviewToggle, rawEditorValue: rawEditorValue, setRawEditorValue: setRawEditorValue, rawEditorRef: rawEditorRef, dataTestId: dataTestId || 'rich-text-editor', allowImages: allowImages, onChange: onChange })), _jsx(EditorContent, { editor: editor, "data-visible": isPreview, "data-testid": `${dataTestId || 'rich-text-editor'}-editor-content`, ...props }), _jsx(TextareaWrapper, { "data-replicated-value": rawEditorValue, "data-visible": !isPreview, children: _jsx(Textarea, { value: rawEditorValue, onChange: handleRawEditorChange, ref: rawEditorRef, "data-testid": `${dataTestId || 'rich-text-editor'}-raw-editor-textarea`, placeholder: placeholder, ...props }) }), editor && (_jsx(BubbleMenu, { editor: editor, shouldShow: shouldShowLinkTooltip, children: _jsx(LinkTooltip, { editor: editor, saveLink: handleSaveLink, placeholder: linkTooltipPlaceholder, dataTestId: dataTestId || 'rich-text-editor' }) }))] }));
});
